import React, { useEffect } from "react";
import { Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import AllEmployees from "../Components/AllEmployees";
import EmployeeDetailsPage from "../Components/EmployeeDetailsPage";
import Expenditure from "../Components/Expenditure";
import HomeRoute from "../Components/HomeRoute";
import LeaveRequestsManagement from "../Components/LeaveManagementRoute";
import LoginForm from "../Components/LoginForm";
import EnterEmail from "../Components/EnterEmail/enterEmail";
import NotFoundRoute from "../Components/NotFoundRoute";
import OTPEnter from "../Components/OtpEnter/otpEnter";
import Payslip from "../Components/Payslip";
import Policies from "../Components/Policies/policies";
import ProfileRoute from "../Components/ProfileRoute";
import ProtectedRoute from "../Components/ProtectedRoute/ProtectedRoute";
import Reimbursement from "../Components/Reimbursement";
import ResetPassword from "../Components/ResetPassword/resetPassword";
import ResourceTracking from "../Components/ResourceTracking/index";
import SalaryManagement from "../Components/SalaryManagement";
import LeavesOverviewManagement from "../Components/LeavesOverviewManagement";
import Settings from "../Components/Settings";
import Tenant from "../Components/Tenants/tenant";
import { ToastContainer } from "react-toastify";
import Projects from "../Components/Projects";
import WorkLogs from "../Components/WorkLogs";
import ProjectRevenueDashBoard from "../Components/ProjectRevenueDashBoard";
import WorkLogsDashBoard from "../Components/WorkLogsDashBoard";
import ProjectUserAssociation from "../Components/ProjectUserAssociation";
import WorkLogsAdminDashBoard from "../Components/WorkLogsAdminDashBoard";
import EmployeesByProject from "../Components/WorkLogsAdminDashBoard/EmployeesByProject";
import InvoiceManagementDashBoard from "../Components/InvoiceManagementDashBoard";
import EmailTemplates from "../Components/EmailTemplates";
import Clients from "../Components/Clients";
import EmailTemplateForm from "../Components/AddEmailTemplate/EmailTemplateForm";
import Form16 from "../Components/Form16";
import RolePermissions from "../Components/RolePermissions";
import Hiring from "../Components/Hiring/JobListPage";
import JobDetailsPage from "../Components/Hiring/JobDetailsPage";
import CandidateDetailsPage from "../Components/Hiring/CandidateDetailsPage";
import EmployeeJobListPage from "../Components/Hiring/EmployeeJobListPage";
import EmployeeJobDetailsPage from "../Components/Hiring/EmployeeJobDetailsPage";
import ReferralDetailsPage from "../Components/Hiring/ReferralDetailsPage";
import toast from "react-hot-toast";
import { usePermissions } from "../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../util/featureCodes";

const ToastManager = () => {
  const location = useLocation();
  useEffect(() => {
    toast.dismiss();
  }, [location.pathname]);
  return null;
};

// /worklogs serves both WorkLogs UIs; the permission decides which one:
//   worklog.view_all -> the all-projects admin dashboard (WorkLogsAdminDashBoard)
//   worklog.view     -> the user's own worklogs dashboard (WorkLogsDashBoard)
// location.state.personalWorklogs lets a view_all holder open their own
// dashboard (the admin dashboard's "Add Worklog" action routes here with it).
// worklog.view is a module baseline every role has (and ProtectedRoute already
// gates the route on it), so in practice this always hits one of the first two
// branches; the NotFoundRoute fallback only covers a fully unpermitted user.
const WorkLogsRoute = () => {
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const wantsPersonalView = location.state?.personalWorklogs === true;

  if (hasPermission(FEATURE_CODES.WORKLOG_VIEW_ALL) && !wantsPersonalView) {
    return <WorkLogsAdminDashBoard path="worklogs_admin_dashboard" />;
  }
  if (hasPermission(FEATURE_CODES.WORKLOG_VIEW)) {
    return <WorkLogsDashBoard path="worklogs" />;
  }
  return <NotFoundRoute />;
};

const AuthenticatedRouter = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginForm />} />
        <Route path="forgot-passwd" element={<EnterEmail />} />
        <Route path="verifyOTP" element={<OTPEnter />} />
        <Route path="reset-passwd" element={<ResetPassword />} />
        {/* <Route path="/test" element={<ForgotPassword />} /> */}
        <Route
          exact
          path="/"
          element={
            <ProtectedRoute>
              <HomeRoute path="/" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfileRoute path="profile" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/allemployees"
          element={
            <ProtectedRoute>
              <AllEmployees path="allemployees" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/employee/:employeeId"
          element={
            <ProtectedRoute>
              <EmployeeDetailsPage path="employee_details" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/employee/:employeeId/:tab"
          element={
            <ProtectedRoute>
              <EmployeeDetailsPage path="employee_details" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/leave_management"
          element={
            <ProtectedRoute>
              <LeaveRequestsManagement path="leave_management" />
              {/* <LeaveRequestsRoute path="leavesrequest"/> */}
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/LeavesOverview"
          element={
            <ProtectedRoute>
              <LeavesOverviewManagement path="LeavesOverview" />
              {/* <LeaveRequestsRoute path="leavesrequest"/> */}
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/salary"
          element={
            <ProtectedRoute>
              <SalaryManagement path="salary" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="payslip/:paramsEmployeeId"
          element={
            <ProtectedRoute>
              <Payslip path="payslip" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/resource_tracking"
          element={
            <ProtectedRoute>
              <ResourceTracking path="resource_tracking" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/reimbursement"
          element={
            <ProtectedRoute>
              <Reimbursement path="reimbursement" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/expenditure"
          element={
            <ProtectedRoute>
              <Expenditure path="expenditure" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings path="settings" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/policies/*"
          element={
            <ProtectedRoute>
              <Policies path="policies" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/projects/:id?"
          element={
            <ProtectedRoute>
              <Projects path="projects" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/clients"
          element={
            <ProtectedRoute>
              <Clients path="clients" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/worklogs"
          element={
            <ProtectedRoute>
              <WorkLogsRoute path="worklogs" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/worklogs/:project_id?/:employee_id?/:year/:month"
          element={
            <ProtectedRoute>
              <WorkLogs path="worklogs" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/worklogs_employee/:project_id/:employee_id"
          element={
            <ProtectedRoute>
              <WorkLogsDashBoard path="worklogs_dashboard" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/revenue"
          element={
            <ProtectedRoute>
              <ProjectRevenueDashBoard path="revenue" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/project/project_details/:id"
          element={
            <ProtectedRoute>
              <ProjectUserAssociation path="project_user_association" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/worklogs_project"
          element={
            <ProtectedRoute>
              <WorkLogsRoute path="worklogs_admin_dashboard" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/worklogs_project/:id"
          element={
            <ProtectedRoute>
              <EmployeesByProject path="worklogs_admin_project_employee_dashboard" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/invoices"
          element={
            <ProtectedRoute>
              <InvoiceManagementDashBoard path="invoices" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/email_templates"
          element={
            <ProtectedRoute>
              <EmailTemplates path="email_templates" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/email_templates/create_template"
          element={
            <ProtectedRoute>
              <EmailTemplateForm path="create_email_template" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/email_templates/edit_template"
          element={
            <ProtectedRoute>
              <EmailTemplateForm path="edit_email_template" />
            </ProtectedRoute>
          }
        />
        <Route
          exact
          path="/form16"
          element={
            <ProtectedRoute>
              <Form16 path="form16" />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/role_permissions/*"
          element={
            <ProtectedRoute>
              <RolePermissions path="role_permissions" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hiring/*"
          element={
            <ProtectedRoute>
              <Hiring path="hiring" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hiring/openings"
          element={
            <ProtectedRoute>
              <EmployeeJobListPage path="hiring_openings" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hiring/openings/:id"
          element={
            <ProtectedRoute>
              <EmployeeJobDetailsPage path="hiring_openings_details" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hiring/my-referrals/:applicationId"
          element={
            <ProtectedRoute>
              <ReferralDetailsPage path="hiring_my_referrals" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hiring/candidate/:candidateId"
          element={
            <ProtectedRoute>
              <CandidateDetailsPage path="hiring_candidate_details" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hiring/job/:id/needs-review"
          element={
            <ProtectedRoute>
              <JobDetailsPage path="hiring_job_needs_review" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hiring/job/:id/duplicate"
          element={
            <ProtectedRoute>
              <JobDetailsPage path="hiring_job_duplicate" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hiring/job/:id/failed"
          element={
            <ProtectedRoute>
              <JobDetailsPage path="hiring_job_failed" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hiring/job/:id/applications"
          element={
            <ProtectedRoute>
              <JobDetailsPage path="hiring_job_applications" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hiring/job/:id"
          element={
            <ProtectedRoute>
              <JobDetailsPage path="hiring_job_details" />
            </ProtectedRoute>
          }
        />
        <Route path="/tenant" element={<Tenant />} />
        <Route path="*" element={<NotFoundRoute />} />
      </Routes>
      <ToastManager />
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={false}
      />
    </Router>
  );
};
export default AuthenticatedRouter;
